import { useEffect, useState } from "react";

/**
 * C1 Transform 2026 post-submit page.
 *
 * This single URL confirms a waitlist submission while retaining the
 * `?status=confirmed` view for any existing confirmation-email links.
 * Calendar and room-block links remain exclusive to confirmed attendees.
 */

const MINT = "#8AF1D9";

interface TransformThankYouProps {
  event_date_label?: string;
  venue_name?: string;
  venue_address?: string;
  cost_label?: string;
  room_block_rate?: string;
  room_block_url?: string;
  event_details_url?: string;
  calendar_url?: string;
}

const eyebrowClasses = "font-display text-[11px] font-bold uppercase tracking-[0.12em] text-gray-60";
const detailLabelClasses = "font-display text-[11px] font-bold uppercase tracking-[0.12em] text-gray-60";

export function TransformThankYou({
  event_date_label = "",
  venue_name = "",
  venue_address = "",
  cost_label = "No cost to attend",
  room_block_rate,
  room_block_url,
  event_details_url = "/c1-transform-2026",
  calendar_url,
}: TransformThankYouProps) {
  // Read the approval-status query param client-side. The vast majority of
  // visits are the default "review" state right after submit; ?status=
  // confirmed only ever comes from a link in a manually-sent approval email,
  // so a brief SSR->hydration flash for that rare case is an acceptable
  // trade against adding route-level search-param parsing for one flag.
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setConfirmed(params.get("status") === "confirmed");
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-cream">
      <div className="bg-white px-4 md:px-8">
        <div className="mx-auto flex max-w-screen-xl items-center justify-between py-5 lg:h-16 lg:py-0">
          <a href="/" className="flex shrink-0 items-center">
            <img src="/images/svg/c1-ai-logo-dark.svg" alt="C1" className="h-[22px] w-auto" />
          </a>
          <div className="font-display text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-60">
            C1 Transform 2026
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center gap-5 bg-teal-dark px-6 py-16 text-center sm:px-10 sm:py-20">
        {confirmed && (
          <span
            className="chip text-charcoal"
            style={{ background: MINT }}
          >
            Confirmed
          </span>
        )}
        <h1 className="text-balance font-display text-[clamp(32px,6vw,57px)] font-semibold leading-[1.02] tracking-tight text-cream">
          {confirmed ? "You're in" : "You're on the waitlist"}
        </h1>
        {confirmed ? (
          <p className="max-w-xl text-pretty text-[17px] leading-relaxed text-cream/80 sm:text-xl">
            {`${event_date_label ? event_date_label + " at " : ""}${venue_name}. Your seat is held — here's what to do now.`}
          </p>
        ) : (
          <p className="max-w-xl text-pretty text-[17px] leading-relaxed text-cream/80 sm:text-xl">
            We’ll notify you if a spot becomes available due to a cancellation.
          </p>
        )}
        {confirmed && (
          <div className="mt-1 flex flex-wrap justify-center gap-3">
            {calendar_url && (
              <a
                href={calendar_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-10 bg-cream px-6 py-2.5 text-base font-medium text-teal-dark transition-colors hover:bg-white"
              >
                Add to calendar
              </a>
            )}
            {room_block_url && (
              <a
                href={room_block_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-10 border border-cream px-6 py-2.5 text-base font-medium text-cream transition-colors hover:bg-cream hover:text-teal-dark"
              >
                Book the room block
              </a>
            )}
          </div>
        )}
      </div>

      {!confirmed && (
        <div className="flex flex-col gap-5 px-6 py-12 sm:px-10 sm:py-14">
          <div className={eyebrowClasses}>What happens next</div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            {[
              {
                n: 1,
                heading: "Keep an eye on your inbox",
                copy: "We’ll email you if a cancellation makes a space available.",
              },
              {
                n: 2,
                heading: "If a spot opens",
                copy: "We’ll send confirmation and the event details you need to attend.",
              },
              {
                n: 3,
                heading: "Explore the agenda",
                copy: "See the speaker line-up and the day’s sessions while you wait.",
              },
            ].map((step) => (
              <div key={step.n} className="flex flex-col gap-3 border border-[#DCDADA] bg-white p-6">
                <div
                  className="flex h-11 w-11 items-center justify-center font-display text-lg font-bold text-teal-dark"
                  style={{ background: MINT }}
                >
                  {step.n}
                </div>
                <div className="font-medium text-[16px] text-teal-dark">{step.heading}</div>
                <div className="text-pretty text-sm leading-relaxed text-charcoal">{step.copy}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-5 px-6 py-12 sm:px-10 sm:py-14">
        <div className={eyebrowClasses}>The details {confirmed ? "" : "so far"}</div>
        <div className="grid grid-cols-1 gap-x-12 sm:grid-cols-2">
          <div className="border-t border-[#DCDADA]">
            <div className="flex items-center justify-between gap-4 border-b border-[#DCDADA] py-3.5">
              <span className={detailLabelClasses}>Date</span>
              <span className="text-[15px] text-teal-dark">{event_date_label}</span>
            </div>
            <div className="flex items-start justify-between gap-4 border-b border-[#DCDADA] py-3.5">
              <span className={detailLabelClasses}>Venue</span>
              <span className="text-right text-[15px] text-teal-dark">
                {venue_name}
                {venue_address ? ` · ${venue_address}` : ""}
              </span>
            </div>
          </div>
          <div className="border-t border-[#DCDADA]">
            <div className="flex items-center justify-between gap-4 border-b border-[#DCDADA] py-3.5">
              <span className={detailLabelClasses}>Cost</span>
              <span className="text-[15px] text-teal-dark">{cost_label}</span>
            </div>
            <div className="flex items-center justify-between gap-4 border-b border-[#DCDADA] py-3.5">
              <span className={detailLabelClasses}>Room block</span>
              <span className="text-right text-[15px] text-teal-dark">
                {room_block_rate}
                {!confirmed && room_block_rate ? ", sent on approval" : ""}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-teal-dark/10 px-6 py-8 sm:px-10">
        <div className="text-pretty text-[13px] text-gray-60">
          {confirmed ? "Need to update anything? Reply to your confirmation email." : null}
        </div>
        {confirmed ? (
          <a href="mailto:events@c1.ai" className="text-[13px] text-teal-dark hover:text-teal-dark-hover">
            Can't make it? Cancel your spot
          </a>
        ) : (
          <a href={event_details_url} className="text-[13px] text-teal-dark hover:text-teal-dark-hover">
            Back to event details
          </a>
        )}
      </div>
    </div>
  );
}
